import { useMemo, useState } from 'react';
import { Link } from 'wouter';
import { ArrowLeft, BookOpen, Check, GitPullRequest, History, X } from 'lucide-react';
import { AdminAPI } from '@shared/types/api';
import { useToast } from '@/components/ux/Toast';
import {
  useReviewTriageProposal,
  useTriagePlaybook,
  useTriagePlaybookRevisions,
  useUpdateTriagePlaybookDoc,
} from '../api/useAdminTriage';
import { RefreshButton, timeAgo } from '../components/ops/opsUi';
import { lineDiff } from '../components/triage/lineDiff';
import RunLearningButton from '../components/triage/RunLearningButton';

// Playbook editor (docs/TRIAGE.md §5/§7): the agent's learned behavior as
// plain markdown docs. Pending proposals get a diff + approve / edit-then-
// approve / reject; docs get a direct editor and a revision history.

const DOC_CHAR_CAP = 12_000;

const DIFF_LINE_STYLES: Record<string, string> = {
  add: 'bg-emerald-50 dark:bg-emerald-900/25 text-emerald-800 dark:text-emerald-200',
  del: 'bg-red-50 dark:bg-red-900/25 text-red-800 dark:text-red-300 line-through decoration-red-400/50',
  same: 'text-dark-muted',
};

const DIFF_LINE_PREFIX: Record<string, string> = { add: '+', del: '-', same: ' ' };

function DiffView({ before, after }: { before: string; after: string }) {
  const lines = useMemo(() => lineDiff(before, after), [before, after]);
  return (
    <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 max-h-80 overflow-auto">
      <pre className="text-[11px] font-mono leading-relaxed p-2 min-w-max">
        {lines.map((line, i) => (
          <div key={i} className={`px-1 ${DIFF_LINE_STYLES[line.type]}`}>
            {DIFF_LINE_PREFIX[line.type]} {line.text || ' '}
          </div>
        ))}
      </pre>
    </div>
  );
}

const REVISION_STATUS_STYLES: Record<string, string> = {
  applied: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  rejected: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};

// ---------------------------------------------------------------------------
// Pending proposal card
// ---------------------------------------------------------------------------

function ProposalCard({
  proposal,
  currentBody,
}: {
  proposal: AdminAPI.TriagePlaybookRevisionInfo;
  currentBody: string;
}) {
  const toast = useToast();
  const review = useReviewTriageProposal();
  const [editing, setEditing] = useState(false);
  const [editedBody, setEditedBody] = useState(proposal.body);
  const [rejecting, setRejecting] = useState(false);
  const [rejectNote, setRejectNote] = useState('');

  const approve = () => {
    review.mutate(
      {
        proposalId: proposal.id,
        action: 'approve',
        ...(editing && editedBody !== proposal.body ? { editedBody } : {}),
      },
      {
        onSuccess: () => toast.success('Proposal approved and applied'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Approve failed'),
      }
    );
  };

  const reject = () => {
    review.mutate(
      {
        proposalId: proposal.id,
        action: 'reject',
        ...(rejectNote.trim() ? { note: rejectNote.trim() } : {}),
      },
      {
        onSuccess: () => toast.success('Proposal rejected'),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Reject failed'),
      }
    );
  };

  return (
    <div className="rounded-xl border border-amber-300 dark:border-amber-700 bg-white dark:bg-neutral-900 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <GitPullRequest className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold">{proposal.slug}</span>
        <span className="text-[11px] text-dark-muted px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
          {proposal.source}
        </span>
        <span className="text-[11px] text-dark-muted">{timeAgo(proposal.createdAt)}</span>
      </div>

      {proposal.rationale && (
        <p className="text-xs text-dark-muted">
          <span className="font-medium">Why:</span> {proposal.rationale}
        </p>
      )}

      {editing ? (
        <textarea
          value={editedBody}
          onChange={(e) => setEditedBody(e.target.value)}
          rows={12}
          className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-xs font-mono leading-relaxed"
        />
      ) : (
        <DiffView before={currentBody} after={proposal.body} />
      )}

      {rejecting ? (
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder="Why not? One line, optional: it feeds the next reflection"
            className="flex-1 min-w-[220px] px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-transparent text-xs"
            autoFocus
          />
          <button
            onClick={reject}
            disabled={review.isPending}
            className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-medium hover:bg-red-700 disabled:opacity-50"
          >
            Reject proposal
          </button>
          <button
            onClick={() => setRejecting(false)}
            className="px-2 py-1.5 rounded-lg text-xs text-dark-muted hover:text-primary-500"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={approve}
            disabled={review.isPending}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check className="w-3.5 h-3.5" />
            {editing ? 'Approve with edits' : 'Approve'}
          </button>
          <button
            onClick={() => setEditing(!editing)}
            className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-primary-400 hover:text-primary-500"
          >
            {editing ? 'Back to diff' : 'Edit before approve'}
          </button>
          <button
            onClick={() => setRejecting(true)}
            disabled={review.isPending}
            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-xs font-medium text-dark-muted hover:border-red-400 hover:text-red-500 disabled:opacity-50"
          >
            <X className="w-3.5 h-3.5" />
            Reject
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Doc card with editor + revisions drawer
// ---------------------------------------------------------------------------

function RevisionRow({ revision }: { revision: AdminAPI.TriagePlaybookRevisionInfo }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="py-1.5 border-t border-neutral-100 dark:border-neutral-800">
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left flex items-center gap-2">
        <span
          className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${
            REVISION_STATUS_STYLES[revision.status] ?? ''
          }`}
        >
          {revision.status}
        </span>
        <span className="text-[11px] text-dark-muted">{revision.source}</span>
        <span className="text-[11px] text-dark-muted ml-auto">{timeAgo(revision.createdAt)}</span>
      </button>
      {expanded && (
        <div className="mt-1.5 space-y-1">
          {revision.rationale && (
            <p className="text-[11px] text-dark-muted">Why: {revision.rationale}</p>
          )}
          {revision.reviewNote && (
            <p className="text-[11px] text-dark-muted">Review note: {revision.reviewNote}</p>
          )}
          <pre className="text-[11px] font-mono leading-relaxed p-2 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700 max-h-60 overflow-auto whitespace-pre-wrap">
            {revision.body}
          </pre>
        </div>
      )}
    </div>
  );
}

function DocCard({ doc }: { doc: AdminAPI.TriagePlaybookDocInfo }) {
  const toast = useToast();
  const updateDoc = useUpdateTriagePlaybookDoc();
  const [body, setBody] = useState(doc.body);
  const [showRevisions, setShowRevisions] = useState(false);
  const { data: revisionsData, isLoading: revisionsLoading } = useTriagePlaybookRevisions(
    showRevisions ? doc.slug : null
  );
  const revisions = revisionsData?.data?.revisions ?? [];

  const dirty = body !== doc.body;
  const charCount = body.length;
  const capRatio = charCount / DOC_CHAR_CAP;
  const meterColor = capRatio > 1 ? 'bg-red-500' : capRatio > 0.8 ? 'bg-amber-400' : 'bg-emerald-500';

  const save = () => {
    updateDoc.mutate(
      { slug: doc.slug, body },
      {
        onSuccess: () => toast.success(`Saved ${doc.slug}`),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Save failed'),
      }
    );
  };

  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-sm font-semibold">{doc.title}</h3>
        <span className="text-[11px] text-dark-muted px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700">
          {doc.slug}
        </span>
        <span className="text-[11px] text-dark-muted ml-auto">updated {timeAgo(doc.updatedAt)}</span>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
          <div
            className={`h-full rounded-full ${meterColor}`}
            style={{ width: `${Math.min(100, capRatio * 100)}%` }}
          />
        </div>
        <span className={`text-[11px] whitespace-nowrap ${capRatio > 1 ? 'text-red-500 font-semibold' : 'text-dark-muted'}`}>
          {charCount.toLocaleString()} / {DOC_CHAR_CAP.toLocaleString()}
        </span>
      </div>

      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={12}
        spellCheck={false}
        className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-xs font-mono leading-relaxed"
      />

      <div className="flex items-center gap-2">
        <button
          onClick={save}
          disabled={!dirty || updateDoc.isPending || charCount > DOC_CHAR_CAP}
          className="px-3 py-1.5 rounded-lg bg-primary-500 text-white text-xs font-medium hover:bg-primary-600 disabled:opacity-50"
        >
          {updateDoc.isPending ? 'Saving…' : 'Save'}
        </button>
        {dirty && (
          <button
            onClick={() => setBody(doc.body)}
            className="px-2 py-1.5 rounded-lg text-xs text-dark-muted hover:text-primary-500"
          >
            Discard changes
          </button>
        )}
        <button
          onClick={() => setShowRevisions(!showRevisions)}
          className="ml-auto inline-flex items-center gap-1 text-xs text-dark-muted hover:text-primary-500"
        >
          <History className="w-3.5 h-3.5" />
          {showRevisions ? 'Hide revisions' : 'Revisions'}
        </button>
      </div>

      {showRevisions && (
        <div>
          {revisionsLoading && <p className="text-[11px] text-dark-muted">Loading…</p>}
          {!revisionsLoading && revisions.length === 0 && (
            <p className="text-[11px] text-dark-muted">No revisions yet.</p>
          )}
          {revisions.map((revision) => (
            <RevisionRow key={revision.id} revision={revision} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminTriagePlaybook() {
  const { data, isLoading, isFetching, refetch } = useTriagePlaybook();
  const docs = data?.data?.docs ?? [];
  const pendingProposals = data?.data?.pendingProposals ?? [];
  const docBySlug = new Map(docs.map((d) => [d.slug, d]));

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4">
        <Link href="/triage" className="text-dark-muted hover:text-primary-500">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <BookOpen className="w-5 h-5 text-primary-500" />
        <h1 className="text-xl font-bold">Triage Playbook</h1>
        <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
        <RunLearningButton className="ml-auto" taskType="triage.bootstrap" />
        <RunLearningButton />
      </div>

      <p className="text-xs text-dark-muted mb-4">
        The agent's voice, rules, and answer library. The agent proposes edits; only approvals here
        change live behavior.
      </p>

      {isLoading && <p className="text-sm text-dark-muted">Loading…</p>}

      {pendingProposals.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold mb-2">
            Pending proposals ({pendingProposals.length})
          </h2>
          <div className="space-y-3">
            {pendingProposals.map((proposal) => (
              <ProposalCard
                key={proposal.id}
                proposal={proposal}
                currentBody={docBySlug.get(proposal.slug)?.body ?? ''}
              />
            ))}
          </div>
        </div>
      )}

      {!isLoading && (
        <div className="space-y-4">
          <h2 className="text-sm font-semibold">Docs</h2>
          {docs.length === 0 && (
            <p className="text-sm text-dark-muted">
              No playbook docs yet. The bootstrap run seeds them, or approve a proposal above.
            </p>
          )}
          {docs.map((doc) => (
            <DocCard key={`${doc.slug}:${doc.updatedAt}`} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
}
